import { createAudioEngine } from '../../audio-engine/core/module-seamless.js';
import type { AudioEngine } from '../types/audio.js';
import { performanceMonitor } from '../../performance-monitor.js';
import { TempoController } from '../../audio-engine/managers/tempo-controller.js';

interface InitializeOptions {
  audioContext: AudioContext;
  wasmModule: WebAssembly.Module;
  trackUrls: string[];
  initialTempo: number;
}

interface PlayOptions {
  lengthInBeats: number;
  lengthInSamples: number;
  timeSignature: number;
}

interface EngineConfig {
  audioContext: AudioContext;
  createDevice: (audioContext: AudioContext) => Promise<any>;
  crossfadeTime: number;
  playbackAudioWorkletUrl: string;
  playbackTempo: number;
  promisedWebAssemblyModule: Promise<WebAssembly.Module>;
  signalsmithStretchAudioWorkletUrl: string;
  workerUrl: string;
  sharedTempoController?: TempoController;
}

/**
 * Coordinates multiple audio engines for synchronized multi-track playback
 */
export class AudioEngineCoordinator {
  private engines: AudioEngine[] = [];
  private audioContext: AudioContext | null = null;
  private isPlayingState = false;
  private isPausedState = false;
  private currentTempo = 120;
  private tracksReady: Map<number, boolean> = new Map();
  private trackUrls: string[] = [];
  private analyserNodes: AnalyserNode[] = [];
  private meterData: Uint8Array[] = [];
  private sharedTempoController: TempoController | null = null;

  constructor() {}

  /**
   * Initialize audio engines for all tracks
   */
  async initialize(options: InitializeOptions): Promise<void> {
    const measure = performanceMonitor.startMeasurement('AudioEngineCoordinator.initialize');

    try {
      this.audioContext = options.audioContext;
      this.currentTempo = options.initialTempo;
      this.trackUrls = options.trackUrls;

      // Create shared tempo controller
      this.sharedTempoController = new TempoController(options.audioContext);
      console.log('🎵 Created shared TempoController for synchronized timing');

      // Clear any existing engines
      this.engines = [];
      this.analyserNodes = [];
      this.meterData = [];
      this.tracksReady.clear();

      // Create audio engines for each track
      for (let i = 0; i < options.trackUrls.length; i++) {
        const engineConfig: EngineConfig = {
          audioContext: options.audioContext,
          createDevice: this.createRNBODevice.bind(this),
          crossfadeTime: 0,
          playbackAudioWorkletUrl: './audio-engine/worklets/simple-playback-worklet-final.js',
          playbackTempo: options.initialTempo,
          promisedWebAssemblyModule: Promise.resolve(options.wasmModule),
          signalsmithStretchAudioWorkletUrl:
            './audio-engine/worklets/signalsmith-stretch-audio-worklet.js',
          workerUrl: './audio-engine/wasm/signalsmith-stretch-worker.js',
          sharedTempoController: this.sharedTempoController
        };

        console.log(`Creating audio engine ${i} for ${options.trackUrls[i]}`);
        const engine = createAudioEngine(engineConfig as any);
        this.engines.push(engine as AudioEngine);

        // Store analyser node if available
        if ((engine as any).analyserNode) {
          this.analyserNodes.push((engine as any).analyserNode);
          const dataArray = new Uint8Array((engine as any).analyserNode.frequencyBinCount);
          this.meterData.push(dataArray);
        }

        // Mark track as not ready yet
        this.tracksReady.set(i, false);

        // Set initial volume to 0 (muted)
        this.engines[i].gain = 0;
      }

      // Preload all tracks
      console.log('🔄 Preloading all audio files...');
      await Promise.all(
        this.engines.map(async (engine, index) => {
          console.log(`📥 Preloading track ${index}: ${options.trackUrls[index]}`);
          await engine.queue(options.trackUrls[index]);
          this.tracksReady.set(index, true);
          console.log(`✅ Track ${index} loaded and ready`);
        })
      );

      console.log('✅ All tracks preloaded and ready for playback');
    } finally {
      performanceMonitor.endMeasurement(measure);
    }
  }

  /**
   * Create RNBO device for audio processing
   */
  private async createRNBODevice(audioContext: AudioContext): Promise<any> {
    // Wait for RNBO to be available
    while (!(window as any).RNBO) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Load the simple passthrough RNBO patch
    const patchResponse = await fetch('./RNBO/simple-thru.export.json');
    const patcherConfig = await patchResponse.json();

    // Create RNBO device
    const RNBO = (window as any).RNBO;
    const device = await RNBO.createDevice({
      context: audioContext,
      patcher: patcherConfig
    });

    console.log('🎛️ RNBO passthrough device created');

    return {
      node: device.node,
      parametersById: device.parametersById || { get: () => null }
    };
  }

  /**
   * Play all tracks with synchronized start time using individual track metadata
   */
  async playAll(options: PlayOptions): Promise<void> {
    const measure = performanceMonitor.startMeasurement('AudioEngineCoordinator.playAll');

    try {
      if (!this.audioContext) {
        throw new Error('Audio context not initialized');
      }

      // Resume context if needed
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Verify all tracks are ready
      const allReady = Array.from(this.tracksReady.values()).every(ready => ready);
      if (!allReady) {
        throw new Error('Not all tracks are ready');
      }

      // Ensure all tracks start at exactly the same time
      const syncDelay = 0.1; // 100ms for worklet setup
      const syncStartTime = this.audioContext.currentTime + syncDelay;

      console.log(
        `🎯 Scheduling all tracks to start at: ${syncStartTime} (current: ${this.audioContext.currentTime})`
      );

      // Each engine will extract its own metadata and calculate correct tempo
      const playPromises = this.engines.map((engine, index) => {
        console.log(`▶️ Starting track ${index}: ${this.trackUrls[index]}`);
        return engine.play({
          // Don't pass lengthInBeats/lengthInSamples/timeSignature - let engine use metadata
          url: this.trackUrls[index],
          when: syncStartTime
        });
      });

      console.log('⏳ Waiting for all tracks to start...');
      await Promise.all(playPromises);
      console.log('✅ All tracks started successfully');

      this.isPlayingState = true;
      this.isPausedState = false;
    } finally {
      performanceMonitor.endMeasurement(measure);
    }
  }

  /**
   * Pause all tracks
   */
  async pauseAll(): Promise<void> {
    await Promise.all(this.engines.map(engine => engine.suspend()));
    this.isPlayingState = false;
    this.isPausedState = true;
  }

  /**
   * Resume all tracks from pause
   */
  async resumeAll(): Promise<void> {
    const resumeMeasure = performanceMonitor.startMeasurement('AudioEngineCoordinator.resumeAll');
    await Promise.all(this.engines.map(engine => engine.resume()));
    performanceMonitor.endMeasurement(resumeMeasure);

    this.isPlayingState = true;
    this.isPausedState = false;
  }

  /**
   * Stop all tracks
   */
  async stopAll(): Promise<void> {
    const stopPromises = this.engines.map(engine => engine.stop());
    await Promise.all(stopPromises);

    this.isPlayingState = false;
    this.isPausedState = false;
  }

  /**
   * Update tempo for all engines using shared controller
   */
  updateTempo(tempo: number): void {
    this.currentTempo = tempo;

    if (!this.audioContext) {
      console.warn('Cannot update tempo: AudioContext not initialized');
      return;
    }

    // Use sample-accurate timing for atomic updates
    const updateTime = this.audioContext.currentTime + 0.01; // 10ms in future

    console.log(`🎵 Updating tempo to ${tempo} BPM at time ${updateTime}`);

    // Update all engines simultaneously using setValueAtTime for precision
    this.engines.forEach((engine, index) => {
      console.log(`🔄 Updating tempo for track ${index}`);
      engine.playbackTempo = tempo;
    });

    console.log('✅ All tracks updated to new tempo');
  }

  /**
   * Set volume for a specific track
   */
  setTrackVolume(trackIndex: number, volume: number): void {
    if (trackIndex < this.engines.length && this.engines[trackIndex]) {
      this.engines[trackIndex].gain = volume;
    }
  }

  // Getters
  getEngines(): AudioEngine[] {
    return this.engines;
  }
  getEngine(index: number): AudioEngine | undefined {
    return this.engines[index];
  }
  getEngineCount(): number {
    return this.engines.length;
  }
  getCurrentTempo(): number {
    return this.currentTempo;
  }
  isPlaying(): boolean {
    return this.isPlayingState;
  }
  isPaused(): boolean {
    return this.isPausedState;
  }
  isTrackReady(index: number): boolean {
    return this.tracksReady.get(index) || false;
  }
  areAllTracksReady(): boolean {
    return Array.from(this.tracksReady.values()).every(ready => ready);
  }
  getAnalyserNodes(): AnalyserNode[] {
    return this.analyserNodes;
  }
  getMeterData(): Uint8Array[] {
    return this.meterData;
  }
}
